import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { Card, Chip, Text } from 'library/ui';
import JobFormPage from './JobFormPage';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { TemplateOption } from 'parent-portal/forms/JobRequestTypes';

const OPTIONS = [
    {
        value: TemplateOption.NEW,
        title: 'Create a new job from scratch',
        subtitle: 'We’ll collect all the info from you to create your job posting.',
    },
    {
        value: TemplateOption.TEMPLATE,
        title: 'Use a previous job post as a template',
        subtitle: 'Re-use a past job and edit any details you’d like.',
        chip: 'Quick Post',
    },
];

export default function SelectJobMethodPage({ name }: { name: string }) {
    const { form, goToNext } = useJobFormContext();
    const { useTemplate } = form.fieldStates;

    return (
        <JobFormPage
            title="Let's create your substitute job"
            onNextClicked={() => goToNext(name)}
            backText="Cancel"
            nextDisabled={false}
            id={name}
        >
            <OptionList>
                {OPTIONS.map((option) => {
                    const selected = useTemplate.value === option.value;
                    return (
                        <OptionCard
                            key={option.value}
                            selected={selected}
                            interactive
                            padding="md"
                            onClick={() => useTemplate.setValue(option.value)}
                        >
                            <OptionRow>
                                <RadioOuter selected={selected}>{selected ? <RadioInner /> : null}</RadioOuter>
                                <TextContainer>
                                    <HeaderRow>
                                        <Text size="md" weight="bold">
                                            {option.title}
                                        </Text>
                                        {option.chip ? (
                                            <Chip
                                                variant="filled"
                                                bgColor={Colors.accent}
                                                textColor={Colors.white}
                                                size="small"
                                            >
                                                {option.chip}
                                            </Chip>
                                        ) : null}
                                    </HeaderRow>
                                    <Text size="sm" tone="secondary">
                                        {option.subtitle}
                                    </Text>
                                </TextContainer>
                            </OptionRow>
                        </OptionCard>
                    );
                })}
            </OptionList>
        </JobFormPage>
    );
}

const OptionCard = styled(Card, { shouldForwardProp: (prop) => prop !== 'selected' })<{ selected: boolean }>(
    ({ selected }) => ({
        '&&': selected ? { backgroundColor: Colors.secondarySoft, borderColor: Colors.secondary } : {},
    }),
);

const OptionList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
    marginTop: 40,
});

const OptionRow = styled('div')({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
});

const RadioOuter = styled('div', { shouldForwardProp: (prop) => prop !== 'selected' })<{ selected: boolean }>(
    ({ selected }) => ({
        width: 22,
        height: 22,
        flexShrink: 0,
        marginTop: 2,
        borderRadius: '50%',
        border: `2px solid ${selected ? Colors.primary : Colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),
);

const RadioInner = styled('div')({
    width: 11,
    height: 11,
    borderRadius: '50%',
    backgroundColor: Colors.primary,
});

const TextContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
});

const HeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 12,
});
